import React, { useRef, useState } from 'react';

import PropTypes from 'prop-types';

import { useDispatch, useSelector } from 'react-redux';

import { push } from 'connected-react-router';

import { Link } from 'react-router-dom';

import { openDashboardDialog } from '@actions/dashboard-actions';
import { openGroupAddEntityDialog } from '@actions/groups-actions';
import { setSelectedRow } from '@actions/data-table-actions';

import * as dialog from '@constants/dialogs';
import { colors } from '@constants/colors';

import AddToGroupIcon from '@icons/add-to-group-icon';
import ShowOnMapIcon from '@icons/show-on-map-icon';
import WorkflowIcon from '@icons/workflow-icon';

import { Icon, IconButton, Menu, MenuItem } from '@mui';

import { entityURLFieldBuilder } from '@utils/entity-utils';
import { renderCustomIconAndLabel } from '@utils/icon-utils';
import { verifyPermissions, canAccessEntity, isReadOnly } from '@utils/permission-utils';

import './actions-menu.scss';

const getIcon = (iconName, index) => {
  if (iconName === 'RestoreIcon') {
    return <Icon aria-hidden="true">restore</Icon>;
  }
  if (iconName === 'AddToGroupIcon') {
    // index makes the icon's internal <path>/<mask> ids unique per row - without it every
    // instance on the page shares the literal id "path-undefined"/"mask-undefined" (duplicate-id).
    return <AddToGroupIcon index={index} />;
  }
  if (iconName === 'WorkflowIcon') {
    return <WorkflowIcon />;
  }
  if (iconName === 'post_add') {
    return <Icon aria-hidden="true">post_add</Icon>;
  }
  if (iconName === 'CopyContentIcon') {
    return <Icon aria-hidden="true">content_copy</Icon>;
  }
  return null;
};

const iconStyle = { padding: '0 0.5rem', width: 'auto' };

// React 17 has no useId() (that's React 18+) - a simple module-level counter gives each
// ActionsMenu instance its own stable id instead, for the aria-controls/id relationship below.
let nextMenuId = 0;
const getNextMenuId = () => {
  nextMenuId += 1;
  return nextMenuId;
};

const ActionsMenu = ({ actions, dataId, dataType, row }) => {
  const { permissions: perms } = useSelector(state => state.config);
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const menuId = useRef(`actions-menu-${getNextMenuId()}`).current;

  const dispatch = useDispatch();

  // The enclosing row has its own Enter/Space keydown handler that navigates to the
  // entity on Enter - without this, pressing Enter on either icon button bubbles up
  // and navigates away instead of activating the button (mouse clicks are unaffected,
  // since the row's click handling is already stopped via event.stopPropagation() in
  // the handlers below).
  const stopRowKeyDown = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.stopPropagation();
    }
  };

  const openMenu = event => {
    event.stopPropagation();
    setOpen(true);
    setAnchorEl(event.currentTarget);
    dispatch(setSelectedRow(dataId));
  };

  const close = event => {
    if (event) {
      event.stopPropagation();
    }
    setOpen(false);
    dispatch(setSelectedRow(null));
  };

  const openOnMap = event => {
    close(event);
    dispatch(push(`/map?${dataType}=${dataId}`));
  };

  const openAddEntityToGroupDialog = event => {
    close(event);
    dispatch(openGroupAddEntityDialog(dataType, row));
  };

  const openAddToCycleDialog = event => {
    close(event);
    const params = { entityId: row.id, reloadEntity: true };
    dispatch(openDashboardDialog(dialog.START_CYCLE, params));
  };

  const createDuplicate = event => {
    close(event);
    dispatch(push(`/${dataType}/${dataId}/duplicate`));
  };

  return (
    <div styleName="actions-menu-container">
      <div styleName="actions-menu-icon">
        <IconButton
          aria-label="Open on map"
          onClick={openOnMap}
          onKeyDown={stopRowKeyDown}
          tooltip="Open on map"
          style={iconStyle}
        >
          <ShowOnMapIcon index={dataId} color={colors.opacity.black0_6} />
        </IconButton>
      </div>
      <div styleName="actions-menu-icon">
        <IconButton
          aria-label="Menu"
          aria-haspopup="true"
          aria-expanded={open}
          aria-controls={menuId}
          onClick={openMenu}
          onKeyDown={stopRowKeyDown}
          style={iconStyle}
        >
          <Icon aria-hidden="true" color={colors.opacity.black0_6}>more_vert</Icon>
        </IconButton>
      </div>
      <Menu
        id={menuId}
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        // Keeps the menu's DOM (and this id) present while closed - otherwise the trigger
        // button's aria-controls references an id that doesn't exist yet (aria-valid-attr-value).
        keepMounted
      >
        {actions && actions.map(({
          allowedEntities,
          dialog: dialogName,
          icon,
          label,
          link,
          permissions,
          url,
          url_fields
        }) => {
          const editable = canAccessEntity(null, dataType, 'add') && !isReadOnly(dataType, perms);

          if ((allowedEntities && !allowedEntities.includes(dataType)) || !verifyPermissions(permissions) || (label === 'Duplicate' && !editable)) {
            return null;
          }

          let onClick = null;
          let text = <div styleName="actions-menu-item">{renderCustomIconAndLabel(label, getIcon(icon, dataId))}</div>;

          if (label === 'Duplicate') {
            onClick = createDuplicate;
          } else
            if (dialogName) {
              if (dialogName === 'add-to-group') {
                onClick = openAddEntityToGroupDialog;
              } else
                if (dialogName === 'add-to-cycle') {
                  onClick = openAddToCycleDialog;
                }
            } else
              if (url) {
                let newUrl = url;
                if (url_fields) {
                  newUrl = entityURLFieldBuilder(url, row, url_fields);
                }
                onClick = () => window.open(newUrl, '_blank');
              } else
                if (link) {
                  onClick = event => event.stopPropagation();
                  text = (
                    <div styleName="actions-menu-item-link">
                      <Link to={`${link}${dataId}`}>{text}</Link>
                    </div>
                  );
                }
          return (
            <MenuItem key={label} onClick={onClick}>
              {text}
            </MenuItem>
          );
        })}
      </Menu>
    </div>
  );
};

ActionsMenu.propTypes = {
  actions: PropTypes.array,
  dataId: PropTypes.number,
  dataType: PropTypes.string,
  row: PropTypes.object
};

export default ActionsMenu;
